// Business card → contact (ideas track, idea 43). The text of a card — read by the browser's text recognition, by the
// platform's OCR provider when the owner configured one, or pasted/typed by the advisor — is split into fields with
// plain rules (no AI, no network): e-mail, phones (Cyprus and international), website, name, job title, company.
// Nothing is saved from here: the advisor reviews every field, then the person is created with the new source
// «γνωριμία / met in person» (client-register.ts `met_in_person`).
import type { Changeset, WorkspaceView } from './changeset.ts';
import { DomainError, clean, type Ctx } from './cmd.ts';
import { createPerson } from './services.ts';
import type { Person, PersonStage } from './schema.ts';

export interface CardFields {
  name: string; firstName: string; lastName: string;
  phone: string; mobile: string; email: string; website: string;
  company: string; jobTitle: string; address: string;
  /** lines the rules did not use (shown to the advisor, kept as a note if he wants) */
  rest: string[];
}

const EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/;
const URL_RE = /\b((?:https?:\/\/)?(?:www\.)[^\s,;]+|https?:\/\/[^\s,;]+|[a-z0-9-]+\.(?:com|cy|gr|eu|net|org|co\.uk|com\.cy)(?:\/[^\s]*)?)\b/i;
const PHONE_CANDIDATE = /(?:\+|00)?\d[\d\s().\/-]{6,}\d/g;
const LABEL = /^(?:t|tel|τηλ|τηλέφωνο|τηλεφωνο|phone|ph|m|mob|mobile|κιν|κινητό|κινητο|cell|f|fax|φαξ|e|email|e-mail|w|web|site)\.?\s*[:.]?\s*/iu;
// hints are matched on the line without accents, in lower case, with letter boundaries that also work for Greek
const hint = (words: string) => new RegExp(`(?<![\\p{L}])(?:${words})`, 'u');
const COMPANY_HINT = hint('ltd|limited|llc|plc|inc|gmbh|s\\.?a\\.?(?![\\p{L}])|ε\\.?π\\.?ε|ο\\.?ε\\.?(?![\\p{L}])|α\\.?ε\\.?(?![\\p{L}])|λτδ|λιμιτεδ|insurance|ασφαλ|bank|τραπεζα|group|holdings|agency|agents|πρακτορε|consult|services|υπηρεσ|solutions|company|εταιρ|& co|συμβουλοι|συμβουλευτικ');
const TITLE_HINT = hint('manager|director|ceo|cfo|coo|cto|founder|owner|partner|advisor|adviser|consultant|agent(?![\\p{L}])|engineer|accountant|lawyer|advocate|doctor|dr\\.|sales|officer|head of|executive|president|assistant|specialist|analyst|architect|συμβουλος|διευθυντ|γενικος|προεδρος|ιδιοκτητ|συνεταιρ|μηχανικ|λογιστ|δικηγ|ιατρ|γιατρ|πωλησε|υπευθυν|ασφαλιστ|εκπροσωπ');
const ADDRESS_HINT = hint('street|st\\.|avenue|ave\\.?|road|rd\\.?|str\\.|οδος|λεωφ|αρ\\.|p\\.?o\\.?\\s*box|τ\\.?θ\\.|nicosia|limassol|larnaca|paphos|famagusta|λευκωσια|λεμεσος|λαρνακα|παφος|αμμοχωστος|cyprus|κυπρος|\\d{4}(?![\\p{N}])');
const plain = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
const has = (re: RegExp, line: string) => re.test(plain(line));
const FAX = /^(?:f|fax|φαξ)\b/iu;
const MOBILE_LABEL = /^(?:m|mob|mobile|κιν|κινητό|κινητο|cell)\b/iu;

/** normalise a phone as written on a card: keeps +, digits and single spaces; Cyprus numbers get +357 */
export function normalizeCardPhone(raw: string): string | null {
  let s = raw.replace(/[()./-]/g, ' ').replace(/\s+/g, ' ').trim();
  if (s.startsWith('00')) s = '+' + s.slice(2);
  const digits = s.replace(/\D/g, '');
  if (digits.length < 8 || digits.length > 15) return null;
  if (!s.startsWith('+') && /^(2|9)\d{7}$/.test(digits)) return `+357 ${digits.slice(0, 2)} ${digits.slice(2)}`; // Cyprus landline / mobile
  if (!s.startsWith('+') && /^(2|69)\d{8}$/.test(digits)) return `+30 ${digits}`; // Greece
  if (s.startsWith('+357') && digits.length === 11) return `+357 ${digits.slice(3, 5)} ${digits.slice(5)}`;
  return s.startsWith('+') ? `+${digits}` : digits;
}
const isMobile = (p: string) => { const d = p.replace(/\D/g, ''); return /^3579\d{7}$/.test(d) || /^3069\d{8}$/.test(d); };

/** split a name into first and last name; ALL-CAPS names are written in title case */
function nameParts(line: string): { name: string; firstName: string; lastName: string } {
  let n = line.replace(/\s+/g, ' ').trim();
  if (n === n.toUpperCase() && /\p{L}/u.test(n)) n = n.toLocaleLowerCase('el').replace(/(^|[\s'-])(\p{L})/gu, (_, a: string, b: string) => a + b.toLocaleUpperCase('el'));
  const parts = n.split(' ').filter((x) => !/^(dr|mr|mrs|ms|κ|κα|δρ)\.?$/i.test(x));
  return { name: parts.join(' '), firstName: parts[0] ?? '', lastName: parts.slice(1).join(' ') };
}
/** a line that looks like a person's name: 2–4 words of letters, no digits, no company/title words */
function looksLikeName(line: string): boolean {
  if (/\d|@|www\.|http/i.test(line) || has(COMPANY_HINT, line) || has(TITLE_HINT, line) || has(ADDRESS_HINT, line)) return false;
  const words = line.trim().split(/\s+/);
  return words.length >= 2 && words.length <= 4 && words.every((w) => /^[\p{L}][\p{L}'.-]*$/u.test(w));
}

/** read the fields of a business card from its text (one item per line) */
export function parseBusinessCard(text: string): CardFields {
  const out: CardFields = { name: '', firstName: '', lastName: '', phone: '', mobile: '', email: '', website: '', company: '', jobTitle: '', address: '', rest: [] };
  const lines = String(text ?? '').split(/\r?\n|\s{3,}|\s*\|\s*/).map((l) => l.replace(/[•·]/g, ' ').replace(/\s+/g, ' ').trim()).filter((l) => l.length > 1);
  const left: string[] = [];
  for (const raw of lines) {
    let line = raw;
    const em = EMAIL.exec(line);
    if (em) { if (!out.email) out.email = em[0].toLowerCase(); line = line.replace(em[0], ' '); }
    const web = URL_RE.exec(line);
    if (web && !EMAIL.test(web[0])) { if (!out.website) out.website = web[0].replace(/[.,]$/, ''); line = line.replace(web[0], ' '); }
    const fax = FAX.test(line.trim());
    const mob = MOBILE_LABEL.test(line.trim());
    const phones = [...line.matchAll(PHONE_CANDIDATE)].map((m) => m[0]);
    for (const ph of phones) {
      const n = normalizeCardPhone(ph);
      if (!n) continue;
      line = line.replace(ph, ' ');
      if (fax) continue;
      if ((mob || isMobile(n)) && !out.mobile) out.mobile = n;
      else if (!out.phone && n !== out.mobile) out.phone = n;
    }
    line = line.replace(LABEL, '').replace(/^[\s:,;-]+|[\s:,;-]+$/g, '').trim();
    if (line.length > 1) left.push(line);
  }
  // name: the first line that looks like a name (cards usually put it first or second)
  const ni = left.findIndex(looksLikeName);
  if (ni >= 0) { Object.assign(out, nameParts(left[ni]!)); left.splice(ni, 1); }
  const ti = left.findIndex((l) => has(TITLE_HINT, l) && !has(COMPANY_HINT, l) && !/\d/.test(l));
  if (ti >= 0) { out.jobTitle = left[ti]!; left.splice(ti, 1); }
  const ci = left.findIndex((l) => has(COMPANY_HINT, l));
  if (ci >= 0) { out.company = left[ci]!; left.splice(ci, 1); }
  const ai = left.findIndex((l) => has(ADDRESS_HINT, l));
  if (ai >= 0) { out.address = left[ai]!; left.splice(ai, 1); }
  // no company word: a remaining short line in capitals is usually the company (logo text)
  if (!out.company) { const k = left.findIndex((l) => l === l.toUpperCase() && /\p{L}{2}/u.test(l) && l.length <= 40); if (k >= 0) { out.company = left[k]!; left.splice(k, 1); } }
  // no name found but an e-mail like maria.georgiou@…: offer that (the advisor confirms)
  if (!out.name && out.email) {
    const local = out.email.split('@')[0]!.split(/[._-]/).filter((x) => /^[a-z]{2,}$/i.test(x));
    if (local.length >= 2) Object.assign(out, nameParts(local.slice(0, 2).map((x) => x[0]!.toUpperCase() + x.slice(1)).join(' ')));
  }
  out.rest = left;
  return out;
}

/** create the person from the REVIEWED card fields — one changeset (person with company/title/phones); source «met in person» */
export function createPersonFromCard(v: WorkspaceView, f: CardFields & { stage?: PersonStage; note?: string | null }, ctx: Ctx): Changeset {
  const displayName = clean(f.name) || [clean(f.firstName), clean(f.lastName)].filter(Boolean).join(' ');
  if (!displayName) throw new DomainError('person.name_required');
  const cs = createPerson(v, {
    displayName, firstName: clean(f.firstName) || undefined, lastName: clean(f.lastName) || undefined, stage: f.stage ?? 'lead',
    primaryPhone: clean(f.mobile) || clean(f.phone) || undefined, primaryEmail: clean(f.email).toLowerCase() || undefined,
    address: clean(f.address) || undefined, source: 'met_in_person', notes: clean(f.note) || undefined,
  }, ctx);
  const personPut = cs.puts[0]!;
  const extra: Partial<Person> = {};
  if (clean(f.company)) extra.employer = clean(f.company).slice(0, 120);
  if (clean(f.jobTitle)) extra.occupation = clean(f.jobTitle).slice(0, 120);
  if (clean(f.mobile) && clean(f.phone)) extra.phones = [{ label: 'mobile', value: clean(f.mobile) }, { label: 'work', value: clean(f.phone) }];
  personPut.row = { ...(personPut.row as Person), ...extra };
  return { ...cs, action: 'person.create_from_card' };
}

/** the note kept on the person for what has no field of its own */
export function cardNote(f: Pick<CardFields, 'website' | 'address' | 'rest' | 'phone' | 'mobile'>, label: { website: string; address: string; phone: string; from: string }): string {
  return [label.from, f.mobile && f.phone ? `${label.phone}: ${f.phone}` : '', f.website ? `${label.website}: ${f.website}` : '', f.address ? `${label.address}: ${f.address}` : '', ...f.rest].filter((x) => clean(x)).join('\n');
}
